import { useState } from "react";
import { Sparkles, Loader2, LayoutGrid } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { autoBuildRoomMoodboard } from "@/api";

const MOODS = ["Minimal", "Cozy", "Dramatic", "Coastal", "Industrial", "Scandinavian", "Japandi", "Luxe", "Biophilic", "Rustic"];

/**
 * Two-mode moodboard builder for a Room:
 *  - "simple"  → deterministic grid (no LLM call, instant)
 *  - "ai"      → uses Gemini for tagline + selected mood palette
 */
export default function RoomMoodboardBuilderDialog({
  open,
  onOpenChange,
  projectId,
  room,
  imageCount = 0,
  onCreated,
}) {
  const [mode, setMode] = useState("simple");
  const [mood, setMood] = useState("Minimal");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleBuild() {
    if (!room) return;
    if (imageCount === 0) {
      toast.error("Link at least one image to this room first");
      return;
    }
    setBusy(true);
    try {
      const mb = await autoBuildRoomMoodboard(projectId, room.id, {
        use_ai: mode === "ai",
        name: name.trim() || `${room.name} moodboard`,
        mood,
      });
      toast.success("Moodboard built");
      onCreated?.(mb);
      onOpenChange(false);
      setName("");
      setMode("simple");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Build failed";
      toast.error(typeof msg === "string" ? msg : "Build failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="max-w-lg"
        data-testid="room-moodboard-builder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Build moodboard for {room?.name || "room"}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            We'll use the {imageCount} image{imageCount === 1 ? "" : "s"} linked
            to this room.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-2 block">
              Mode
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("simple")}
                data-testid="room-mb-mode-simple"
                className={`text-left p-3 rounded-xl border transition-all ${
                  mode === "simple"
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-200 hover:border-stone-400 bg-white"
                }`}
              >
                <LayoutGrid className="w-4 h-4 mb-1" strokeWidth={1.5} />
                <p className="font-medium text-sm">Simple grid</p>
                <p
                  className={`text-xs mt-0.5 ${
                    mode === "simple" ? "text-stone-300" : "text-stone-500"
                  }`}
                >
                  Instant clean grid layout
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMode("ai")}
                data-testid="room-mb-mode-ai"
                className={`text-left p-3 rounded-xl border transition-all ${
                  mode === "ai"
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-200 hover:border-stone-400 bg-white"
                }`}
              >
                <Sparkles className="w-4 h-4 mb-1" strokeWidth={1.5} />
                <p className="font-medium text-sm">With AI</p>
                <p
                  className={`text-xs mt-0.5 ${
                    mode === "ai" ? "text-stone-300" : "text-stone-500"
                  }`}
                >
                  Mood palette + AI tagline
                </p>
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-2 block">
              Name (optional)
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`${room?.name || "Room"} moodboard`}
              data-testid="room-mb-name-input"
            />
          </div>

          {mode === "ai" && (
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-2 block">
                Mood
              </label>
              <div className="flex flex-wrap gap-1.5" data-testid="room-mb-mood-row">
                {MOODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMood(m)}
                    data-testid={`room-mb-mood-${m.toLowerCase()}`}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      mood === m
                        ? "bg-stone-900 text-white"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleBuild}
            disabled={busy || imageCount === 0}
            data-testid="room-mb-build-submit"
            className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : mode === "ai" ? (
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
            ) : (
              <LayoutGrid className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            Build moodboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
